<script setup lang="ts">
import { computed, onBeforeMount, PropType, ref, Ref } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useBaseStore } from "@/stores/base";
import PermissionStructureDTO = App.Data.PermissionStructureDTO;
import PermissionDO = App.Data.PermissionDO;
import { t } from "@/plugins/i18n";
import router from "@/router/router";

/**
 * v-treeview alternative to PermissableManager.vue.
 *
 * Differences vs. the expansion-panel version:
 *  - One recursive item tree instead of triplicated markup; arbitrary depth.
 *  - Trade-off: per-row loading spinner / lock-guard live in the #append slot
 *    rather than in a dedicated PermissionRow switch.
 *
 * Permission leaves are loaded lazily (see `loadChildren` below) rather than
 * materialized up front — v-treeview registers every item it's given, even
 * collapsed ones, into its internal selection/indeterminate tracking on
 * mount, and doing that for the full (potentially large) permission set is
 * what was hanging the tab on load.
 *
 * Because branch (group/domain/family) descendants aren't all materialized,
 * v-treeview can't compute an accurate checked/indeterminate state for them
 * itself (select-strategy="classic" needs the real child items registered to
 * do that). So select-strategy is "leaf" — v-treeview only ever renders a
 * native checkbox for loaded leaves — and branch rows get their own checkbox
 * in the #prepend slot below, driven by grantedCount/availableCount (which
 * read the underlying permission data directly, not the lazily-loaded tree)
 * and wired to a manual grant-all/revoke-all action instead of v-treeview's
 * cascade.
 */
const props = defineProps({
	availablePermissions: {
		type: Object as PropType<Array<string>>,
		require: true,
	},
	derivedPermissions: {
		type: Object as PropType<Array<string>>,
		required: true,
	},
	asyncUpdateFunction: {
		type: Function,
		required: true,
	},
	asyncBulkUpdateFunction: {
		type: Function,
		required: false,
		default: null,
	},
	permissionLockGuard: {
		type: Boolean,
		required: false,
		default: true,
	},
	readOnly: {
		type: Boolean,
		required: false,
		default: false,
	},
});

const derivedPermissions = ref(props.derivedPermissions);

const baseStore = useBaseStore();
const loadingState = ref<string[]>([]);
const permissionStructure: Ref<PermissionStructureDTO> = ref(null);

const loadDependencies = async () => {
	const permissionStructureResponse = await useApiClient(
		"environment"
	).getPermissionStructure();
	if (permissionStructureResponse.isOk()) {
		permissionStructure.value = permissionStructureResponse.value;
	}
};

onBeforeMount(() => {
	loadDependencies();
});

// --- node helpers (unchanged from the panel version) ---------------------
type PermissionNode = PermissionDO[] | { [key: string]: PermissionNode };

const flatten = (node: PermissionNode): PermissionDO[] =>
	Array.isArray(node) ? node : Object.values(node).flatMap(flatten);

const availableIn = (node: PermissionNode): PermissionDO[] => {
	const available = flatten(node).filter((p) =>
		props.availablePermissions.includes(p.name)
	);
	return [...new Map(available.map((p) => [p.name, p])).values()];
};

const availableCount = (node: PermissionNode): number =>
	availableIn(node).length;

const grantedCount = (node: PermissionNode): number =>
	availableIn(node).filter((p) => derivedPermissions.value.includes(p.name))
		.length;

const hasAvailable = (node: PermissionNode): boolean =>
	availableIn(node).length > 0;

const isBranchChecked = (node: PermissionNode): boolean => {
	const total = availableCount(node);
	return total > 0 && grantedCount(node) === total;
};

const isBranchIndeterminate = (node: PermissionNode): boolean => {
	const granted = grantedCount(node);
	return granted > 0 && granted < availableCount(node);
};

// Grants/revokes every available (non-locked) permission under a branch in
// one go — the manual equivalent of v-treeview's classic-strategy cascade,
// needed because that cascade requires materialized descendant items.
const toggleBranch = async (node: PermissionNode) => {
	if (props.readOnly) return;
	const add = !isBranchChecked(node);
	const names = availableIn(node)
		.map((p) => p.name)
		.filter((name) => !isLocked(name))
		.filter((name) => derivedPermissions.value.includes(name) !== add);
	if (names.length === 0) return;
	await persist(names, add);
};

// --- tree item construction ----------------------------------------------
type TreeItem = {
	id: string;
	title: string;
	name?: string; // permission name (leaves only); id is unique per tree position
	subtitle?: string; // permission description (leaves only)
	node?: PermissionNode;
	permission?: PermissionDO;
	disabled: boolean;
	children?: TreeItem[];
};

// A group is rendered nested (domain > family) when it actually has property
// domains. Groups without them (e.g. "app", "administration") only ever have
// the placeholder "unknown" domain/family and render as a flat list.
const hasDomainStructure = (domains: PermissionNode): boolean =>
	!Array.isArray(domains) &&
	Object.keys(domains).some((domain) => domain !== "unknown");

const stripCanViewPrefix = (name: string) =>
	name.startsWith("can_view_property_") ? name.slice(18) : name;

const isLocked = (name: string): boolean =>
	router.currentRoute.value.meta.permissions.includes(name) &&
	props.permissionLockGuard &&
	!props.readOnly;

const leafTitle = (p: PermissionDO): string =>
	p.type === "data"
		? t(`properties.${stripCanViewPrefix(p.name)}.title`)
		: t(`permissions.${p.name}.title`);

const leafSubtitle = (p: PermissionDO): string =>
	p.type === "data"
		? t(`properties.${stripCanViewPrefix(p.name)}.description`)
		: t(`permissions.${p.name}.description`);

const leafItems = (perms: PermissionDO[], prefix: string): TreeItem[] =>
	[...new Map(perms.map((p) => [p.name, p])).values()]
		.filter((p) => props.availablePermissions.includes(p.name))
		.map((p) => ({
			id: `${prefix}:${p.name}`, // unique per position; a permission can recur across domains
			name: p.name,
			title: leafTitle(p),
			subtitle: leafSubtitle(p),
			permission: p,
			disabled: props.readOnly || isLocked(p.name),
		}));

/**
 * Branches that would otherwise hold real permission leaves (families, and
 * groups with no domain structure) get an empty `children: []` placeholder
 * here instead of the materialized leaf list. v-treeview treats a present-
 * but-empty `children` array as "has children, not loaded yet" and shows an
 * expand arrow; feeding it thousands of real leaf items up front is what was
 * hanging the tab — v-treeview eagerly registers every item (even collapsed
 * ones) into its internal selection/indeterminate maps on mount. Real leaves
 * are loaded on demand by `loadChildren` the first time a branch is opened,
 * see `loadedChildren`/`displayItems` below.
 */
const items = computed<TreeItem[]>(() => {
	if (!permissionStructure.value) return [];
	return Object.entries(permissionStructure.value.definition)
		.filter(([, domains]) => hasAvailable(domains))
		.map(([groupName, domains]) => {
			const children = hasDomainStructure(domains as PermissionNode)
				? Object.entries(domains)
						.filter(([, families]) => hasAvailable(families))
						.map(([domainName, families]) => ({
							id: `domain:${groupName}:${domainName}`,
							title: t(`property_domains.${domainName}.title`),
							node: families as PermissionNode,
							disabled: false,
							children: Object.entries(families as PermissionNode)
								.filter(([, perms]) => hasAvailable(perms))
								.map(([familyName, perms]) => ({
									id: `family:${groupName}:${domainName}:${familyName}`,
									title: t(`property_families.${familyName}.title`),
									node: perms,
									disabled: false,
									children: [] as TreeItem[],
								})),
						}))
				: [];
			return {
				id: `group:${groupName}`,
				title: t(`permissions.groups.${groupName}`),
				node: domains as PermissionNode,
				disabled: false,
				children,
			};
		});
});

// id -> materialized leaves, populated lazily by loadChildren as branches are opened.
const loadedChildren = ref(new Map<string, TreeItem[]>());

// Merges `items` with whatever has been loaded so far. A fresh computed
// (rather than mutating tree nodes in place) keeps `items` safe to recompute
// independently and avoids relying on deep-mutation reactivity of nested,
// non-ref-owned objects.
const displayItems = computed<TreeItem[]>(() => {
	const attach = (nodes: TreeItem[]): TreeItem[] =>
		nodes.map((node) => {
			if (node.children && node.children.length > 0) {
				return { ...node, children: attach(node.children) };
			}
			if (node.node && loadedChildren.value.has(node.id)) {
				return { ...node, children: loadedChildren.value.get(node.id) };
			}
			return node;
		});
	return attach(items.value);
});

const loadChildren = async (rawItem: TreeItem): Promise<void> => {
	if (!rawItem.node || loadedChildren.value.has(rawItem.id)) return;
	loadedChildren.value.set(
		rawItem.id,
		leafItems(flatten(rawItem.node), rawItem.id)
	);
};

// Map each unique leaf id -> its permission name (a name may map from several ids).
// Only covers leaves materialized so far — see loadedChildren above.
const leafIdToName = computed<Map<string, string>>(() => {
	const map = new Map<string, string>();
	const walk = (list: TreeItem[]) => {
		list.forEach((i) => {
			if (i.name) map.set(i.id, i.name);
			if (i.children) walk(i.children);
		});
	};
	walk(displayItems.value);
	return map;
});

// Selected = every (materialized) leaf id whose permission is granted.
const selected = computed<string[]>(() =>
	[...leafIdToName.value.entries()]
		.filter(([, name]) => derivedPermissions.value.includes(name))
		.map(([id]) => id)
);

// --- persistence ----------------------------------------------------------
const applyLocal = (name: string, add: boolean) => {
	if (add) {
		if (!derivedPermissions.value.includes(name))
			derivedPermissions.value.push(name);
	} else {
		const index = derivedPermissions.value.indexOf(name);
		if (index !== -1) derivedPermissions.value.splice(index, 1);
	}
};

const clearLoading = (names: string[]) => {
	setTimeout(() => {
		names.forEach((name) => {
			const i = loadingState.value.indexOf(name);
			if (i !== -1) loadingState.value.splice(i, 1);
		});
	}, baseStore.loadingDefaultDelay);
};

/**
 * Persist a set of same-direction changes. A single change goes through the
 * single-permission endpoint; multiple changes use the bulk endpoint when the
 * consumer provides one, otherwise they fall back to parallel single calls.
 * State is updated optimistically and every affected row shows a spinner.
 */
const persist = async (names: string[], add: boolean) => {
	if (names.length === 0) return;

	const method = add ? "grant" : "revoke";
	names.forEach((name) => {
		if (!loadingState.value.includes(name)) loadingState.value.push(name);
		applyLocal(name, add);
	});

	if (names.length === 1) {
		await toastApiResult(props.asyncUpdateFunction({ method, name: names[0] }));
	} else if (props.asyncBulkUpdateFunction) {
		await toastApiResult(props.asyncBulkUpdateFunction({ method, names }));
	} else {
		await Promise.all(
			names.map((name) =>
				toastApiResult(props.asyncUpdateFunction({ method, name }))
			)
		);
	}

	clearLoading(names);
};

/**
 * Treeview emits the full desired leaf-id selection. We diff it against the
 * previous id selection (`selected`) to find exactly which leaves the user
 * toggled, then map those ids to permission names. Diffing ids — not names — is
 * essential: a permission in several domains renders as several leaves sharing
 * one name, so unchecking one instance must register even while its siblings
 * stay checked. The resulting name then grants/revokes every instance at once.
 * A leaf toggle yields one change; a parent toggle cascades to many.
 */
const onSelect = async (newSelected: unknown) => {
	if (props.readOnly) return;

	const newIds = new Set(newSelected as string[]);
	const prevIds = new Set(selected.value);

	const grant = new Set<string>();
	const revoke = new Set<string>();

	const nameFor = (id: string): string | undefined => {
		const name = leafIdToName.value.get(id);
		return name && !isLocked(name) ? name : undefined;
	};

	newIds.forEach((id) => {
		if (prevIds.has(id)) return;
		const name = nameFor(id);
		if (name) grant.add(name);
	});
	prevIds.forEach((id) => {
		if (newIds.has(id)) return;
		const name = nameFor(id);
		if (name) revoke.add(name);
	});

	// A name toggled both ways in one emit nets to no change; drop the overlap.
	[...grant].forEach((name) => {
		if (revoke.has(name)) {
			grant.delete(name);
			revoke.delete(name);
		}
	});

	await Promise.all([persist([...grant], true), persist([...revoke], false)]);
};
</script>

<template>
	<v-alert
		v-if="availablePermissions.length === 0"
		border="start"
		variant="tonal"
		color="error"
		:title="t('permissions.noPermissionsWarningTitle')"
	>
		{{ t("permissions.noPermissionsWarningText") }}
	</v-alert>

	<v-treeview
		v-if="permissionStructure"
		:items="displayItems"
		:load-children="loadChildren"
		item-value="id"
		item-title="title"
		item-children="children"
		:selectable="!readOnly"
		select-strategy="leaf"
		:selected="selected"
		open-on-click
		density="comfortable"
		@update:selected="onSelect"
	>
		<!-- v-treeview passes the raw item as the slot's `item` scope -->
		<!-- branches get their own checkbox here since select-strategy="leaf"
		     suppresses v-treeview's native one for them (see script comment) -->
		<template #prepend="{ item }">
			<v-checkbox-btn
				v-if="item.node && !readOnly"
				:model-value="isBranchChecked(item.node)"
				:indeterminate="isBranchIndeterminate(item.node)"
				density="comfortable"
				@click.stop="toggleBranch(item.node)"
			/>
		</template>

		<template #subtitle="{ item }">
			<div
				v-if="item.permission && item.subtitle"
				class="permission-description text-medium-emphasis pb-1"
			>
				{{ item.subtitle }}
			</div>
		</template>

		<!-- leaf icon / branch chevron handled by treeview; add per-row status -->
		<template #append="{ item }">
			<!-- leaf: loading spinner or lock icon -->
			<template v-if="item.permission">
				<v-progress-circular
					v-if="loadingState.includes(item.name)"
					indeterminate
					size="18"
					width="2"
					color="primary"
				/>
				<v-tooltip v-else-if="item.disabled && !readOnly" location="top">
					<template #activator="{ props: tip }">
						<i v-bind="tip" class="fa-duotone fa-lock-keyhole text-primary"></i>
					</template>
					{{ t("permissions.locked") }}
				</v-tooltip>
			</template>

			<!-- branch: granted / available count -->
			<span v-else-if="item.node" class="text-small text-medium-emphasis">
				{{ grantedCount(item.node) }} / {{ availableCount(item.node) }}
			</span>
		</template>
	</v-treeview>
</template>

<style lang="scss" scoped>
// v-list-item subtitles are single-line/ellipsis by default; let descriptions wrap.
.permission-description {
	white-space: normal;
	line-height: 1.3;
	font-size: 0.8125rem;
}

// Selected leaves otherwise get the app-wide secondary-tinted "activated"
// background (see .v-list-item--active in style.scss), with text color left
// as `inherit` — against a white row that inherited color turns unreadable.
// The checkbox already communicates selection, so drop the row highlight
// entirely and force a readable text color instead.
:deep(.v-list-item--active) {
	background-color: rgb(var(--v-theme-surface)) !important;
	color: rgb(var(--v-theme-on-surface)) !important;
	background-image: none !important;
}
</style>
